export function ErrorSection() {
  return (
    <section className="error-section" data-testid="error-section">
      <div className="container">
        <p className="eyebrow reveal">El error más frecuente</p>
        <div className="error-contrast reveal">
          <div className="error-left">
            <span className="error-tag">Lo que muchos creen</span>
            <p className="error-belief">«Muchos terapeutas creen que necesitan aprender otra técnica.»</p>
            <p className="error-note">Pero normalmente ese no es el problema.</p>
          </div>
          <div className="error-right" data-testid="error-highlight">
            <span className="error-tag dark">El verdadero problema</span>
            <p>No saber <strong>cuándo</strong> utilizar cada herramienta.</p>
          </div>
        </div>
        <p className="error-middle reveal">
          Una misma intervención puede ser profundamente reparadora para un paciente…
          <span> y completamente prematura para otro.</span>
        </p>
        <div className="error-final reveal">
          <p className="error-big">La diferencia no está en la técnica.<br /><em>Está en el criterio.</em></p>
          <p className="error-sub">Y ese criterio puede aprenderse.</p>
        </div>
      </div>
    </section>
  );
}

const MODELS = ["EMDR", "IFS", "Teoría Polivagal", "Modelos somáticos", "Narrativa", "DBT"];

export function TwoPaths() {
  return (
    <section className="paths-section" data-testid="paths-section">
      <div className="container">
        <p className="eyebrow reveal">Dos puntos de partida, un mismo mapa</p>
        <div className="paths-grid">
          <article className="path-card reveal" data-testid="path-card-advanced">
            <h3>Si ya has estudiado trauma…</h3>
            <p>Este curso te ayudará a integrar todo lo que ya sabes.</p>
            <div className="model-tags">
              {MODELS.map((m, i) => (
                <span className="model-tag" key={i}>{m}</span>
              ))}
            </div>
            <p>Dejarán de parecer piezas independientes.</p>
            <p className="path-emphasis">Empezarán a formar un único mapa.</p>
          </article>
          <article className="path-card reveal" data-testid="path-card-beginner">
            <h3>Si todavía estás empezando en trauma…</h3>
            <p className="path-emphasis">También es para ti.</p>
            <p>No necesitas conocer modelos complejos. Ni memorizar protocolos.</p>
            <p>Empezaremos construyendo una forma de pensar.</p>
            <p>Porque cuando entiendes el mapa… aprender cualquier técnica después resulta mucho más sencillo.</p>
          </article>
        </div>
      </div>
    </section>
  );
}

const DISCOVER = [
  "Qué observé.",
  "Qué hipótesis formulé.",
  "Qué descarté.",
  "Qué señales me hicieron cambiar de estrategia.",
  "Qué errores habría cometido si hubiera intervenido demasiado pronto.",
];

function CaseCard({ num }) {
  return (
    <article className="case-card-simple reveal">
      <span className="case-label">Caso clínico</span>
      <h3>Caso {num}</h3>
      <div className="case-card-lines" aria-hidden="true">
        <span />
        <span />
        <span />
      </div>
    </article>
  );
}

export function Cases() {
  return (
    <section className="cases-section" data-testid="cases-section">
      <div className="container">
        <div className="cases-layout">
          <div className="cases-copy">
            <h2 className="section-title cases-title reveal" data-testid="cases-title">
              Tres pacientes. Tres historias.<br />
              <span>Tres formas completamente diferentes de intervenir.</span>
            </h2>
            <div className="cases-introduction reveal">
              <p>No vas a estudiar únicamente teoría.</p>
              <p>Entrarás conmigo en tres casos reales.</p>
              <p>Verás exactamente cómo razona una terapeuta especializada.</p>
            </div>
            <div className="case-cards-grid" data-testid="case-cards-grid">
              <CaseCard num="01" />
              <CaseCard num="02" />
              <CaseCard num="03" />
            </div>
          </div>
          <div className="cases-visual reveal" aria-hidden="true" />
        </div>
        <div className="cases-detail">
          <p className="cases-close reveal" data-testid="cases-close">
            No aprenderás únicamente <em>qué</em> hice.
            <span>Aprenderás <em>por qué</em> lo hice.</span>
          </p>
          <div className="discover-card reveal" data-testid="discover-list">
            <p className="discover-title">Descubrirás…</p>
            <ul>
              {DISCOVER.map((d, i) => (
                <li key={i}>
                  <i className="discover-dot" aria-hidden="true" />
                  {d}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

const CONTRASTS = [
  ["No aprenderás un protocolo.", "Aprenderás una manera de pensar."],
  ["No memorizarás pasos.", "Aprenderás a formular mejores preguntas."],
  [
    "No dependerás de una técnica concreta.",
    "Desarrollarás un criterio clínico que podrás aplicar durante toda tu carrera profesional.",
  ],
];

export function Different() {
  return (
    <section className="different-section" data-testid="different-section">
      <div className="container">
        <p className="eyebrow reveal">Lo que hace diferente este curso</p>
        <div className="contrasts">
          {CONTRASTS.map((c, i) => (
            <div className="contrast-row reveal" key={i} data-testid={`contrast-row-${i + 1}`}>
              <p className="contrast-no">{c[0]}</p>
              <span className="contrast-arrow" aria-hidden="true">→</span>
              <p className="contrast-yes">{c[1]}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const FOR_YOU = [
  "Eres psicólogo, psicoterapeuta o profesional de la salud mental.",
  "Trabajas —o quieres trabajar— con personas que han vivido experiencias traumáticas.",
  "Buscas claridad cuando aparecen casos complejos.",
  "Quieres integrar todo lo que ya sabes dentro de un único marco clínico.",
  "Deseas sentirte más seguro tomando decisiones terapéuticas.",
];

const NOT_FOR_YOU = [
  "Buscas recetas rápidas.",
  "Esperas un protocolo universal.",
  "Crees que una única técnica sirve para todos los pacientes.",
];

export function ForYou() {
  return (
    <section className="foryou-section" data-testid="foryou-section">
      <div className="container">
        <div className="foryou-grid">
          <div className="foryou-block reveal" data-testid="foryou-yes-block">
            <h2 className="section-title">Este curso es para ti si…</h2>
            <ul className="check-list">
              {FOR_YOU.map((f, i) => (
                <li key={i}>
                  <span className="check-icon" aria-hidden="true">
                    <svg viewBox="0 0 16 16" width="16" height="16" fill="none">
                      <path d="M2.5 8.5 6 12l7.5-8" stroke="#5f7858" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                  {f}
                </li>
              ))}
            </ul>
          </div>
          <div className="notforyou-block reveal" data-testid="foryou-no-block">
            <h3>Probablemente no sea para ti si…</h3>
            <ul className="cross-list">
              {NOT_FOR_YOU.map((f, i) => (
                <li key={i}>
                  <span className="cross-icon" aria-hidden="true">
                    <svg viewBox="0 0 14 14" width="13" height="13" fill="none">
                      <path d="M3 3l8 8M11 3l-8 8" stroke="#9aa5a0" strokeWidth="1.6" strokeLinecap="round" />
                    </svg>
                  </span>
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <p className="foryou-close reveal" data-testid="foryou-close">
          El trauma no funciona así.
          <span> Las personas tampoco.</span>
        </p>
      </div>
    </section>
  );
}
